import { useState } from "react";

import { EmailContentType, Emptyable } from "@simplyk/common";
import { useForm, useWatch } from "react-hook-form";

import { useRecipientPlaceholder } from "../../EmailRecipientEditor/useRecipientPlaceholder";

import { useTranslate } from "@/hooks/useTranslate";
import { useCurrentUserContext } from "@/src/contexts/CurrentUserContext";
import { EmailEditorFormData } from "@/types/email";

type UseFooterActionsProps = {
  footerActions?: EmailFooterAction[];
  form: ReturnType<typeof useForm<EmailEditorFormData>>;
  handleSave: () => void;
  handleSendEmail: () => void;
  handleSchedule: () => void;
  loading?: boolean;
  emailType: Emptyable<EmailContentType>;
};

export type EmailFooterAction = Partial<Omit<RichEmailFooterAction, "onClick">> | RichEmailFooterAction["key"];

export type EmailFooterProps = {
  actions: RichEmailFooterAction[];
  disableActions: boolean;
  closeEmailEditor: () => void;
};

export type RichEmailFooterAction = {
  key: "save" | "send" | "schedule" | "send-test";
  label: string;
  onClick: ((data: EmailEditorFormData) => void) | (() => void);
  disabled?: boolean;
  tooltipText?: string;
  isLoading?: boolean;
};

export const useEmailFooter = ({
  footerActions,
  form,
  handleSave,
  handleSendEmail,
  handleSchedule,
  loading,
  emailType,
}: UseFooterActionsProps) => {
  const { t } = useTranslate();

  const [openSendTestDialog, setOpenSendTestDialog] = useState(false);

  const recipientEmails = useWatch({ control: form.control, name: "recipientEmails" });
  const subject = useWatch({ control: form.control, name: "subject" });

  const recipientsCount = recipientEmails?.length || 0;
  const { currentUser, isSendingEmailForbidden, isStripeCustomAccountActive } = useCurrentUserContext();
  const organizationWithoutStripeAccountHasSentTooMuchEmail = recipientsCount > 500 && !isStripeCustomAccountActive;

  const recipientPlaceholder = useRecipientPlaceholder({ emailType, hasRecipients: Boolean(recipientsCount) });

  // If there is a recipient placeholder, it means the recipients will be dynamically added to the email
  const sendNowOrScheduleDisabled =
    (recipientsCount === 0 && !recipientPlaceholder) || organizationWithoutStripeAccountHasSentTooMuchEmail || !subject;

  const defaultActions: RichEmailFooterAction[] = [
    {
      key: "send",
      onClick: handleSendEmail,
      label: t("dashboard", "tickets.sendNow"),
      disabled: sendNowOrScheduleDisabled,
      tooltipText:
        recipientsCount === 0
          ? t("dashboard", "emailDrawerAction.sendNow.tooltipRecipients")
          : !subject
            ? t("dashboard", "emailDrawerAction.sendNow.tooltipSubject")
            : undefined,
      isLoading: loading,
    },
    {
      key: "schedule",
      onClick: handleSchedule,
      label: t("dashboard", "EmailDrawer.actions.scheduledForLater"),
      disabled: sendNowOrScheduleDisabled,
      tooltipText:
        recipientsCount === 0
          ? t("dashboard", "emailDrawerAction.sendNow.tooltipRecipients")
          : !subject
            ? t("dashboard", "emailDrawerAction.sendNow.tooltipSubject")
            : undefined,
      isLoading: loading,
    },
    {
      key: "save",
      onClick: handleSave,
      label: t("dashboard", "EmailDrawer.actions.save"),
      disabled: false,
      isLoading: loading,
    },
    {
      key: "send-test",
      onClick: () => {
        setOpenSendTestDialog(true);
      },
      label: t("dashboard", "tickets.testEmail"),
      disabled: !subject || isSendingEmailForbidden,
      tooltipText: isSendingEmailForbidden
        ? t("dashboard", "common.mustCompleteBankToEnableEmailing") +
          " " +
          t("dashboard", "common.mustCompleteBankToEnableEmailing.reason")
        : undefined,
    },
  ];

  const actions: RichEmailFooterAction[] = footerActions
    ? footerActions.reduce((acc, action) => {
        if (typeof action !== "string") {
          const defaultAction = defaultActions.find((defaultAction) => defaultAction.key === action.key);

          return [...acc, { ...defaultAction, ...action } as RichEmailFooterAction];
        }

        const defaultAction = defaultActions.find((defaultAction) => defaultAction.key === action);
        if (defaultAction) {
          return [...acc, defaultAction];
        }

        return acc;
      }, [] as RichEmailFooterAction[])
    : defaultActions;

  return {
    actions,
    disableActions: Boolean(!currentUser?.emailVerified || isSendingEmailForbidden),
    openSendTestDialog,
    setOpenSendTestDialog,
  };
};
